import { NavLink, Redirect, useParams } from "react-router-dom";
import { LuArrowLeftRight, LuBan, LuBot, LuKeyboard, LuKeyRound, LuListChecks, LuMail, LuPuzzle, LuUsers } from "react-icons/lu";
import ApiKeysSection from "../components/settings/ApiKeysSection";
import UsersSection from "../components/settings/UsersSection";
import VisibleFieldsSection from "../components/settings/VisibleFieldsSection";
import DataEntrySection from "../components/settings/DataEntrySection";
import PatientEmailsSection from "../components/settings/PatientEmailsSection";
import HiddenCodesSection from "../components/settings/HiddenCodesSection";
import IgnoredTermsSection from "../components/settings/IgnoredTermsSection";
import RobotSection from "../components/settings/RobotSection";
import TransferSection from "../components/settings/TransferSection";
import ExtensionSection from "../components/settings/ExtensionSection";

const SECTION_GROUPS = [
  { label: "Espace", sections: [{ id: "users", label: "Utilisateurs", Icon: LuUsers }] },
  {
    label: "Dossiers",
    sections: [
      { id: "fields", label: "Champs visibles", Icon: LuListChecks },
      { id: "data-entry", label: "Saisie", Icon: LuKeyboard },
      { id: "ignored-terms", label: "Termes et codes ignorés", Icon: LuBan },
    ],
  },
  { label: "Communication", sections: [{ id: "patient-emails", label: "Emails patients", Icon: LuMail }] },
  {
    label: "Intégrations",
    sections: [
      { id: "api-keys", label: "Clés d'API", Icon: LuKeyRound },
      { id: "robot", label: "Connexion robot", Icon: LuBot },
      { id: "extension", label: "Extension Chrome", Icon: LuPuzzle },
    ],
  },
  { label: "Super admin", sections: [{ id: "transfer", label: "Import / Export", Icon: LuArrowLeftRight, superAdminOnly: true }] },
];

const Settings = ({ user }) => {
  const { section = "users" } = useParams();
  const isSuperAdmin = user.role === "superadmin";
  const groups = SECTION_GROUPS.map((group) => ({ ...group, sections: group.sections.filter((item) => !item.superAdminOnly || isSuperAdmin) })).filter(
    (group) => group.sections.length > 0,
  );

  if (!groups.some((group) => group.sections.some((item) => item.id === section))) return <Redirect to="/settings" />;

  return (
    <div className="flex h-[calc(100vh-60px)] flex-col bg-[#eef0f3] md:flex-row">
      <nav aria-label="Paramètres" className="flex shrink-0 gap-x-4 gap-y-5 overflow-x-auto px-3 py-3 md:w-60 md:flex-col md:overflow-x-visible md:py-5">
        <h1 className="px-2 pb-1 text-[15px] font-semibold text-gray-900 max-md:hidden">Paramètres</h1>
        {groups.map((group) => (
          <div key={group.label} className="flex gap-0.5 md:flex-col">
            <p className="px-2 pb-1.5 text-xs font-medium text-gray-400 max-md:hidden">{group.label}</p>
            {group.sections.map(({ id, label, Icon }) => (
              <NavLink
                key={id}
                to={`/settings/${id}`}
                isActive={() => id === section}
                className="group flex h-8 items-center gap-2.5 whitespace-nowrap rounded-md px-2 text-[13px] font-medium text-gray-600 transition-colors hover:bg-gray-950/5 hover:text-gray-900"
                activeClassName="!bg-white text-gray-900 shadow-sm ring-1 ring-gray-950/5"
              >
                <Icon className="size-4 shrink-0 text-gray-400 transition-colors group-hover:text-gray-600 group-aria-[current=page]:text-gray-700" />
                {label}
              </NavLink>
            ))}
          </div>
        ))}
      </nav>

      <div className="mx-2 mb-2 min-w-0 flex-1 overflow-y-auto rounded-xl bg-gray-50 shadow-[0_1px_3px_rgba(0,0,0,0.06)] ring-1 ring-gray-950/[0.06] md:ml-0 md:mt-2">
        <div className="mx-auto max-w-[52rem] px-4 py-8 sm:px-8 sm:py-12">
          {section === "api-keys" && <ApiKeysSection />}
          {section === "users" && <UsersSection user={user} />}
          {section === "fields" && <VisibleFieldsSection />}
          {section === "data-entry" && <DataEntrySection />}
          {section === "patient-emails" && <PatientEmailsSection />}
          {section === "ignored-terms" && (
            <div>
              <div className="mb-8">
                <h2 className="text-2xl font-semibold tracking-tight text-gray-900">Termes et codes ignorés</h2>
                <p className="mt-1.5 text-sm text-gray-500">{"Ce qui n'est pas un examen : des mots sur l'ordonnance, des codes dans le SIL. Querco n'en tient pas compte."}</p>
              </div>
              <div className="space-y-10">
                <IgnoredTermsSection />
                <HiddenCodesSection />
              </div>
            </div>
          )}
          {section === "robot" && <RobotSection />}
          {section === "extension" && <ExtensionSection />}
          {section === "transfer" && <TransferSection />}
        </div>
      </div>
    </div>
  );
};

export default Settings;
